/**
 * Observability context — the small handle the rest of the app reads.
 *
 * Lives in its own file so the React context object stays distinct from
 * the provider component. That separation lets test files and future
 * consumers import the context type without dragging in the (`"use client"`
 * gated) provider module.
 *
 * The value shape stays stable across PRs:
 *   - PR-2 (this PR): ``posthog`` is always ``null``; ``isEnabled`` reads
 *     env wiring so downstream code can branch correctly even while the
 *     SDK isn't initialised yet.
 *   - PR-3: ``posthog`` becomes the live posthog-js instance once
 *     ``posthog.init()`` runs inside the provider.
 *
 * See ``plans/PLAN_POSTHOG_01_FOUNDATIONS.md`` § "Provider architecture".
 */

import { createContext, useContext } from "react";
import type posthog from "posthog-js";

export interface ObservabilityContextValue {
  /**
   * The posthog-js singleton, or ``null`` when the SDK has not been
   * initialised. PR-2 always returns ``null``; PR-3 flips this to the
   * actual library once ``posthog.init()`` runs.
   */
  posthog: typeof posthog | null;
  /**
   * Whether PostHog would capture if asked. Resolved from
   * ``NEXT_PUBLIC_POSTHOG_ENABLED`` + the presence of a project key.
   * Stable across the lifetime of the provider — env vars don't change
   * at runtime in Next.js.
   */
  isEnabled: boolean;
}

export const ObservabilityContext =
  createContext<ObservabilityContextValue | null>(null);

/**
 * Read the observability context. Throws if used outside the provider —
 * a misuse here means the app tree was rendered without the provider
 * mounted, which would silently break analytics downstream.
 */
export function useObservability(): ObservabilityContextValue {
  const value = useContext(ObservabilityContext);
  if (value === null) {
    throw new Error(
      "useObservability() called outside <ObservabilityProvider>. " +
        "Wrap your component tree in <ObservabilityProvider> (see " +
        "src/lib/providers.tsx).",
    );
  }
  return value;
}
